import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { GameType } from '@sportninja/common/constants/GameType'
import type { GeneratedGame } from '../components/GeneratedGamesSection'
import type { Venue } from '../hooks/useSchedulerLocations'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Escapes a CSV value per RFC 4180.
 * Wraps in double quotes if value contains comma, quote, or newline.
 */
function escapeCsvValue(value: string): string {
  if (
    value.includes(',') ||
    value.includes('"') ||
    value.includes('\n') ||
    value.includes('\r')
  ) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/**
 * SN-7977: the file is uploaded on the competition's Games tab, so it is shaped
 * for that import. There is no SN Game ID: the column held each draft row's
 * internal id, which the import rejected as an unknown game, so the file
 * created nothing. Teams go out under the full name the import matches.
 */
const CSV_HEADER =
  'Number,Home,Visitor,Date,Time,Location,Facility,City,Game Type,Hierarchy'

/** "Regular Season", the type publication gives the scheduler's games. */
const GAME_TYPE = GameType[2]

/**
 * Generates a CSV string from generated games and venues.
 */
export function generateGamesCsv({
  games,
  venues,
}: {
  games: GeneratedGame[]
  venues: Venue[]
}): string {
  const cityMap = new Map<string, string>()
  for (const venue of venues) {
    if (venue.address?.city) {
      cityMap.set(venue.id, venue.address.city)
    }
  }

  // Sort by the flight-unique global number (matching the on-screen order) so the
  // export doesn't interleave divisions on their colliding per-division numbers.
  const sorted = [...games].sort(
    (a, b) =>
      (a.globalGameNumber ?? a.gameNumber) -
      (b.globalGameNumber ?? b.gameNumber)
  )

  const rows = sorted.map((game, index) => {
    const tz = game.timezone || 'UTC'
    const date = game.startsAt
      ? dayjs.utc(game.startsAt).tz(tz).format('YYYY-MM-DD')
      : ''
    const time = game.startsAt
      ? dayjs.utc(game.startsAt).tz(tz).format('h:mm A')
      : ''
    const city = game.locationId ? cityMap.get(game.locationId) ?? '' : ''

    return [
      String(index + 1),
      game.homeTeamImportName || game.homeTeam,
      game.awayTeamImportName || game.awayTeam,
      date,
      time,
      game.location,
      game.surface,
      city,
      GAME_TYPE,
      game.division,
    ]
      .map(escapeCsvValue)
      .join(',')
  })

  return [CSV_HEADER, ...rows].join('\n')
}

/**
 * Downloads a CSV string as a file.
 */
export function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const a = document.createElement('a')
  document.body.appendChild(a)
  a.style.display = 'none'
  const url = URL.createObjectURL(blob)
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
  a.remove()
}
